import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { fmt12 } from "@/lib/time";
import { TrendingUp, Users, Calendar, Wallet, IndianRupee, Home as HomeIcon, Smartphone } from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar,
} from "recharts";

export default function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    const url = user?.role === "master_admin" ? "/admin/dashboard/master" : "/admin/dashboard/branch";
    api.get(url).then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  if (!data) return <div className="text-emerald-400 font-bold text-sm tracking-widest uppercase animate-pulse p-10">Loading Admin Dashboard...</div>;

  const isMaster = user?.role === "master_admin";

  return (
    <div data-testid="admin-dashboard" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex items-baseline justify-between border-b-2 border-white/10 pb-6"
      >
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1">
            {isMaster ? "MASTER OVERVIEW CONSOLE" : "BRANCH CONSOLE"}
          </div>
          <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
            {isMaster ? "Business Overview" : data.branch?.name}
          </h1>
        </div>
        <div className="text-xs text-amber-300/80 font-semibold uppercase tracking-widest bg-amber-950/30 px-4 py-2 rounded-xl border border-amber-400/30">
          {new Date().toLocaleDateString("en-IN", { weekday: "short", year: "numeric", month: "short", day: "numeric" })}
        </div>
      </motion.div>

      {/* KPI Cards Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-5"
      >
        {isMaster ? (
          <>
            <Kpi label="Total Revenue" value={inr(data.total_revenue)} icon={IndianRupee} border="border border-red-500/80 shadow-red-950/30" textColor="text-red-400" iconColor="text-red-400" bgGradient="bg-gradient-to-b from-red-950/50 via-black/90 to-red-950/20" />
            <Kpi label="Today's Revenue" value={inr(data.today_revenue)} icon={TrendingUp} sub={`${data.today_bookings} bookings`} border="border border-emerald-400/80 shadow-emerald-950/30" textColor="text-emerald-400" iconColor="text-emerald-400" bgGradient="bg-gradient-to-b from-emerald-950/50 via-black/90 to-emerald-950/20" />
            <Kpi label="This Month" value={inr(data.month_revenue)} icon={Calendar} border="border border-amber-400/80 shadow-amber-950/30" textColor="text-amber-400" iconColor="text-amber-400" bgGradient="bg-gradient-to-b from-amber-950/50 via-black/90 to-amber-950/20" />
            <Kpi label="Pending Payouts" value={inr(data.pending_payouts)} icon={Wallet} border="border border-rose-400/80 shadow-rose-950/30" textColor="text-rose-400" iconColor="text-rose-400" bgGradient="bg-gradient-to-b from-rose-950/50 via-black/90 to-rose-950/20" />
            <Kpi label="Cash Collection" value={inr(data.cash_collection)} icon={IndianRupee} border="border border-hgold/80 shadow-yellow-950/30" textColor="text-hgold" iconColor="text-hgold" bgGradient="bg-gradient-to-b from-yellow-950/50 via-black/90 to-yellow-950/20" />
            <Kpi label="Online Collection" value={inr(data.online_collection)} icon={Smartphone} border="border border-cyan-400/80 shadow-cyan-950/30" textColor="text-cyan-400" iconColor="text-cyan-400" bgGradient="bg-gradient-to-b from-cyan-950/50 via-black/90 to-cyan-950/20" />
            <Kpi label="Doorstep Revenue" value={inr(data.doorstep_revenue)} icon={HomeIcon} border="border border-purple-500/80 shadow-purple-950/30" textColor="text-purple-400" iconColor="text-purple-400" bgGradient="bg-gradient-to-b from-purple-950/50 via-black/90 to-purple-950/20" />
            <Kpi label="Active Therapists" value={data.therapist_count} icon={Users} sub={`across ${data.branch_count} branches`} border="border border-teal-400/80 shadow-teal-950/30" textColor="text-teal-400" iconColor="text-teal-400" bgGradient="bg-gradient-to-b from-teal-950/50 via-black/90 to-teal-950/20" />
          </>
        ) : (
          <>
            <Kpi label="Total Revenue" value={inr(data.total_revenue)} icon={IndianRupee} border="border border-purple-500/80 shadow-purple-950/30" textColor="text-purple-400" iconColor="text-purple-400" bgGradient="bg-gradient-to-b from-purple-950/50 via-black/90 to-purple-950/20" />
            <Kpi label="Today's Revenue" value={inr(data.today_revenue)} icon={IndianRupee} border="border border-red-500/80 shadow-red-950/30" textColor="text-red-400" iconColor="text-red-400" bgGradient="bg-gradient-to-b from-red-950/50 via-black/90 to-red-950/20" />
            <Kpi label="Today's Bookings" value={data.today_bookings} icon={Calendar} sub={`${data.today_walkins} walk-ins`} border="border border-emerald-400/80 shadow-emerald-950/30" textColor="text-emerald-400" iconColor="text-emerald-400" bgGradient="bg-gradient-to-b from-emerald-950/50 via-black/90 to-emerald-950/20" />
            <Kpi label="Pending Bookings" value={data.pending_bookings} icon={Calendar} border="border border-amber-400/80 shadow-amber-950/30" textColor="text-amber-400" iconColor="text-amber-400" bgGradient="bg-gradient-to-b from-amber-950/50 via-black/90 to-amber-950/20" />
            <Kpi label="Therapists Active" value={data.therapist_count} icon={Users} border="border border-teal-400/80 shadow-teal-950/30" textColor="text-teal-400" iconColor="text-teal-400" bgGradient="bg-gradient-to-b from-teal-950/50 via-black/90 to-teal-950/20" />
            <Kpi label="Today's Cash" value={inr(data.today_cash)} icon={IndianRupee} border="border border-hgold/80 shadow-yellow-950/30" textColor="text-hgold" iconColor="text-hgold" bgGradient="bg-gradient-to-b from-yellow-950/50 via-black/90 to-yellow-950/20" />
            <Kpi label="Today's Online" value={inr(data.today_online)} icon={Smartphone} border="border border-cyan-400/80 shadow-cyan-950/30" textColor="text-cyan-400" iconColor="text-cyan-400" bgGradient="bg-gradient-to-b from-cyan-950/50 via-black/90 to-cyan-950/20" />
          </>
        )}
      </motion.div>

      {/* Charts Grid */}
      {isMaster && data.weekly_revenue && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-6"
        >
          <div className="rounded-2xl border border-amber-400/60 p-6 bg-gradient-to-b from-amber-950/30 via-black/95 to-hpaper shadow-xl shadow-amber-950/20">
            <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold mb-4">REVENUE · LAST 7 DAYS</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.weekly_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="date" stroke="#a3a3a3" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis stroke="#a3a3a3" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #fbbf24", borderRadius: "12px", color: "#fff", fontWeight: "bold" }} />
                <Line type="monotone" dataKey="revenue" stroke="#f59e0b" strokeWidth={3} dot={{ fill: "#fbbf24", r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl border border-emerald-400/60 p-6 bg-gradient-to-b from-emerald-950/30 via-black/95 to-hpaper shadow-xl shadow-emerald-950/20">
            <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-4">BRANCH PERFORMANCE</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.branch_performance}>
                <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
                <XAxis dataKey="name" stroke="#a3a3a3" tick={{ fontSize: 11 }} tickFormatter={(v) => v.split(" ").slice(-2).join(" ")} />
                <YAxis stroke="#a3a3a3" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #34d399", borderRadius: "12px", color: "#fff", fontWeight: "bold" }} />
                <Bar dataKey="revenue" fill="#34d399" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      )}

      {/* Upcoming Appointments (For Branch Admin) */}
      {!isMaster && data.upcoming && (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
          className="rounded-2xl border-2 border-hgold/70 p-6 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20"
        >
          <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-extrabold mb-4">UPCOMING APPOINTMENTS</div>
          <div className="space-y-3">
            {data.upcoming.map((b) => (
              <div key={b.id} className="flex justify-between items-center py-3.5 border-b border-white/10 px-2 rounded-lg hover:bg-white/5 transition-all">
                <div>
                  <div className="text-white font-bold">{b.customer_name}</div>
                  <div className="text-xs text-white/60 mt-1 font-medium">{b.date} · {fmt12(b.time)} · <span className="text-emerald-400 font-bold uppercase">{b.booking_type}</span></div>
                </div>
                <div className="text-amber-400 font-heading font-bold text-lg">{inr(b.amount)}</div>
              </div>
            ))}
            {data.upcoming.length === 0 && <div className="text-white/60 text-sm py-4">No upcoming appointments scheduled today.</div>}
          </div>
        </motion.div>
      )}
    </div>
  );
}

function Kpi({ label, value, icon: Icon, sub, border, textColor, iconColor, bgGradient }) {
  return (
    <div className={`p-6 rounded-2xl ${bgGradient || "bg-gradient-to-b from-hpaper via-black/90 to-hpaper"} transition-all duration-300 hover:scale-[1.02] ${border}`}>
      <div className="flex justify-between items-start mb-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-white/70">{label}</div>
        {Icon && <Icon size={20} className={`${iconColor || "text-white/40"} shrink-0`} strokeWidth={2} />}
      </div>
      <div className={`font-heading text-2xl lg:text-3xl font-extrabold tracking-tight ${textColor || "text-white"}`}>{value}</div>
      {sub && <div className="text-xs font-medium text-white/60 mt-2">{sub}</div>}
    </div>
  );
}
